import { Suspense, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { PerspectiveCamera, WebGPURenderer } from 'three/webgpu';
import type { Renderer } from '@react-three/fiber';

import { useAppStore } from '../store/appStore';
import { useLoadStore } from '../store/loadStore';
import { unlockAudio } from '../audio/context';
import { Effects } from './Effects';
import { Indicators } from './Indicators';
import { Instrument } from './Instrument';
import { Intro } from './Intro';
import { KeyGuide } from './KeyGuide';
import { Lights } from './Lights';
import { ScreenDriver } from './ScreenDriver';
import { SetPiece } from './SetPiece';
import { Warmup } from './Warmup';
import { ScreenTexture } from './screen/ScreenTexture';
import { useEnvironment } from './useEnvironment';

/**
 * `WebGPURenderer` picks the WebGPU backend when the browser has one and falls
 * back to WebGL2 on its own. TSL compiles to both, so the whole material and
 * post-processing stack runs either way.
 *
 * `?webgl` forces the fallback path, which is the only practical way to compare
 * the two backends on a machine that has WebGPU.
 */
async function buildRenderer(canvas: HTMLCanvasElement) {
  const forceWebGL =
    typeof location !== 'undefined' && new URLSearchParams(location.search).has('webgl');

  const renderer = new WebGPURenderer({
    canvas,
    antialias: false,
    alpha: false,
    powerPreference: 'high-performance',
    forceWebGL,
  });
  await renderer.init();
  return renderer as unknown as Renderer;
}

/**
 * One renderer per canvas, however many times R3F asks for one.
 *
 * R3F reads `state.gl` before awaiting this factory and writes it after, and the
 * effect that calls it has no dependency array — so every render of the canvas
 * that lands while the backend is still initialising starts a second call that
 * still sees no renderer. Warm, that window is too short to hit; cold, with the
 * models parsing and the samples decoding alongside it, it is wide open, and the
 * second renderer reconfigures the canvas out from under the post chain the
 * first one is already bound to, which draws the scene into nothing until a
 * resize rebuilds it.
 *
 * Handing every caller the same promise closes the window: whichever call wins
 * installs the renderer the others are already holding.
 */
const renderers = new WeakMap<HTMLCanvasElement, Promise<Renderer>>();

function createRenderer(props: { canvas: unknown }): Promise<Renderer> {
  const canvas = props.canvas as HTMLCanvasElement;

  const existing = renderers.get(canvas);
  if (existing) return existing;

  // A failed init is not cached: the canvas is still free for a later attempt.
  const pending = buildRenderer(canvas).catch((error: unknown) => {
    renderers.delete(canvas);
    throw error;
  });
  renderers.set(canvas, pending);
  return pending;
}

function World({ screen }: { screen: ScreenTexture }) {
  useEnvironment();
  const phase = useAppStore((state) => state.phase);

  return (
    <>
      <Lights />
      <Intro />
      {/* Mounted from the warm-up onward and only hidden in between, rather
          than mounted when it is first wanted: the instrument clones its own
          materials on mount, so remounting it would throw away the pipelines
          compiled behind the loader and compile them again mid-reveal. */}
      <group visible={phase !== 'intro'}>
        <SetPiece />
        <Instrument screen={screen}>
          <Indicators screen={screen} />
          <KeyGuide />
        </Instrument>
      </group>
      <Warmup />
    </>
  );
}

/**
 * Our own camera rather than a description of one.
 *
 * Given a description, R3F builds the camera inside `configure`, off the same
 * stale state snapshot that made it build renderers twice — so an overlapping
 * call constructs a second camera and installs it. Only the size-change branch
 * of R3F's store subscription ever calls `updateCamera`, and by then the size
 * has already settled, so that second camera keeps the `aspect` of `0` it was
 * constructed with. A zero aspect makes the projection matrix infinite and
 * every vertex NaN, which draws nothing and reports nothing; the first resize
 * finally sets the aspect and the scene appears.
 *
 * Passing an instance takes the construction away from `configure` entirely:
 * every call installs this one object, and R3F keeps its aspect up to date from
 * then on because it is not marked manual.
 */
function createCamera(): PerspectiveCamera {
  const camera = new PerspectiveCamera(60, 1, 0.1, 2000);
  camera.position.set(0, 0, 6);
  return camera;
}

export function Stage() {
  const screen = useMemo(() => new ScreenTexture(), []);
  // Sized from the window up front so the very first frame is drawn through a
  // finite projection, whether or not a resize has landed by then.
  const camera = useMemo(() => {
    const camera = createCamera();
    camera.aspect = window.innerWidth / Math.max(1, window.innerHeight);
    camera.updateProjectionMatrix();
    return camera;
  }, []);
  const start = useAppStore((state) => state.start);
  const assetsReady = useLoadStore((state) => state.assetsReady);

  return (
    <Canvas
      id="stage"
      gl={createRenderer}
      camera={camera}
      dpr={[1, 2]}
      flat
      onPointerDown={(event) => {
        void unlockAudio();
        // Screen UV, with y from the top down, which is the frame the burn is
        // laid out in: the title card goes back out through the point clicked.
        const { left, top, width, height } = event.currentTarget.getBoundingClientRect();
        start({ x: (event.clientX - left) / width, y: (event.clientY - top) / height });
      }}
    >
      {/* The canvas goes up first and renders empty while the loader fetches:
          that is what gets the renderer created and the post chain compiled in
          parallel with the download rather than after it. The world waits for
          the files, so its loaders read them back out of the cache. */}
      <Suspense fallback={null}>{assetsReady && <World screen={screen} />}</Suspense>
      <ScreenDriver screen={screen} />
      <Effects />
    </Canvas>
  );
}
